import React, {useCallback, useState, useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import useThreeSixtyNewStore from 'common/stores/threeSixtyStore';
import {shallow} from 'zustand/shallow';

import {
	StyledAiLayout,
	StyledHeaderWarning,
	StyledNpsActionLayout,
	StyledNpsDivider,
	StyledNpsGrid,
	StyledNpsNameLayout,
	StyledNpsSatisfactionLayout,
	StyledShevronButton,
	StyledShevronIcon,
} from './styled';
import {
	Button,
	Divider,
	IconButton,
	Layout,
	ListItem,
	Select,
	Text,
	Tooltip,
	WarningIcon,
} from '@phonectas/unifon-ui-kit';
import NewCopyIcon from 'common/components/icons/NewCopyIcon';
import CustomerLinks from '../CustomerLinks';
import CustomerCompanyInfo from '../CustomerCompanyInfo';
import CustomerDevices from '../CustomerDevices';
import {formatInfo} from 'utils/div';
import {useSmallScreen} from 'common/hooks/useScreenSize';
import {useCopyToClipboard} from 'common/hooks/useCopyToClipboard';
import {npsScrore} from 'utils/div';
import SmallViewDetails from './components/SmallViewDetails';
import TrustpilotDialog from 'common/components/TrustpilotDialog';
import NewOkIcon from 'common/components/icons/NewOkIcon';
import ByttDialog from 'common/components/ByttDialog';
import {Account, ThreeSixtyStoreState} from 'common/types/store/threeSixtyTypes';
import CrownIcon from 'common/components/icons/CrownIcon';
import UpsellBtn from 'common/components/UpsellBtn';
import UppsellContent from 'common/components/UpsellContent/UppsellContent';
import UpsellDialog from 'common/components/UpsellDialog';
import InnsalgDialog from 'common/components/InnsalgDialog';
import InnsalgNotice from 'common/components/InnsalgNotice';
import AiSparkleIcon from 'common/components/icons/AiSparkleIcon';
import {useLocation, useParams} from 'react-router-dom';

interface CustomerDetailsProps {
	data: Account;
}

const CustomerDetails: React.FC<CustomerDetailsProps> = ({data}) => {
	const [t] = useTranslation();
	const params = useParams<{id: string}>();
	const location = useLocation();
	const accountDetails = data?.account_details;
	const isSmallSCreenWidth = useSmallScreen('(max-width:1600px)');
	const isSmallSCreen = useSmallScreen('(max-height:800px)');
	// temporary flag to hide the upsell button until we have the content ready and the correct upsell id logic in place
	const disableUpsell = true;
	const {
		setSatisfactionRating,
		getAccount,
		getAccountFeed,
		getInnsalg,
		resetInnsalg,
	} = useThreeSixtyNewStore((state) => ({
		setSatisfactionRating: state.setSatisfactionRating,
		getAccount: state.getAccount,
		getAccountFeed: state.getAccountFeed,
		getInnsalg: state.getInnsalg,
		resetInnsalg: state.resetInnsalg,
	}), shallow);
	const {
		callerInfo,
		account,
		isContactPerson,
		upsellDetails,
		eventFilter,
		innsalg,
		innsalgFor,
	} = useThreeSixtyNewStore((state: ThreeSixtyStoreState) => ({
		callerInfo: state?.bnRedirect?.user,
		account: state?.bnRedirect?.account,
		isContactPerson: state?.bnRedirect?.account?.account_details?.user_details?.is_contact_person,
		upsellDetails: state?.upsellDetails,
		eventFilter: state?.pageFilters?.eventFeed,
		innsalg: state?.innsalg,
		innsalgFor: state?.innsalgOrgnr,
	}), shallow);
	const [showContent, setShowContent] = useState(false);
	const [showTrustpilot, setShowTrustpilot] = useState(false);
	const [showBytt, setShowBytt] = useState(false);
	const [showUpsell, setShowUpsell] = useState(false);
	const [correctUpsellId, setCorrectUpsellId] = useState(false);
	const [showInnsalg, setShowInnsalg] = useState(false);
	const [innsalgNotice, setInnsalgNotice] = useState<string | null>(null);
	const innsalgCheckedFor = useRef<string | null>(null);
	const innsalgToastedFor = useRef<string | null>(null);
	const isContactPersonCalling = Boolean(data?.id && data.id === callerInfo?.account_id && isContactPerson);

	const onShowUpsell = useCallback((e: React.SyntheticEvent) => {
		e.stopPropagation();
		setShowUpsell(true);

	}, []);
	const onHideUpsell = useCallback(() => setShowUpsell(false), []);
	const onHideInnsalg = useCallback(() => setShowInnsalg(false), []);
	const onHideInnsalgNotice = useCallback(() => setInnsalgNotice(null), []);
	const onShowBytt = useCallback(() => setShowBytt(true), []);
	const onHideBytt = useCallback(() => setShowBytt(false), []);
	const onShowTrustpilot = useCallback(() => setShowTrustpilot(true), []);
	const onHideTrustpilot = useCallback(() => setShowTrustpilot(false), []);


	const onShowContent = useCallback(() => {
		setShowContent((prev) => !prev);
		if (isSmallSCreenWidth) {
			// for future use
		}
	}, [isSmallSCreenWidth]);
	const isNpsScore = npsScrore(0);
	// const isNpsScore = npsScrore(accountDetails?.nps);
	const [selectValue, setSelectValue] = useState('');
	const [copyToClipboard] = useCopyToClipboard();
	const handleCopyCustomerName = useCallback((e: React.SyntheticEvent) => {
		e.stopPropagation();
		copyToClipboard(accountDetails?.name);
	}, [accountDetails?.name, copyToClipboard]);
	const options = Array.from({length: 11}, (_, i) => (i.toString()));

	const handleSatisfactionChange = useCallback(
		(
			val: string | number | string[] | number[] | null | undefined,

			_e: React.SyntheticEvent,

			_name: string
		) => {
			let value: string | undefined;
			if (Array.isArray(val)) {
				value = val.length > 0 ? String(val[0]) : undefined;
			} else if (val !== null && val !== undefined) {
				value = String(val);
			}
			if (!value) return;
			setSelectValue(value);
			setSatisfactionRating({
				accountId: data?.id,
				body: value,
				onSuccess: () => {
					getAccount({id: data?.id, phone: null});
					getAccountFeed({
						id: data?.id,
						feedType: eventFilter ?? [],
					});
				},
				// Show the saved value again, the new one was not stored
				onError: () => setSelectValue(accountDetails?.satisfaction_rating),
			});
		},
		[getAccount, data?.id, setSatisfactionRating, getAccountFeed, eventFilter, accountDetails?.satisfaction_rating]
	);

	useEffect(() => {
		if (isSmallSCreen) {
			setShowContent(true);
		} else {
			setShowContent(false);
		}
	}, [isSmallSCreen]);

	useEffect(() => {
		setSelectValue(accountDetails?.satisfaction_rating);
	}, [accountDetails?.satisfaction_rating]);

	useEffect(() => {
		if (params?.id && upsellDetails?.account_id) {
			setCorrectUpsellId(params.id === String(upsellDetails.account_id));
			return;
		}
		setCorrectUpsellId(false);
	}, [params?.id, upsellDetails?.account_id]);
 
	const innsalgOrgnr = (data?.vat_number || accountDetails?.vat_number || '').replace(/\D/g, '');
	const canLookupInnsalg = innsalgOrgnr.length === 9;
	// The store can still hold the previous customer's answer (or a late one), only use it for this org.nr.
	const activeInnsalg = innsalgFor && innsalgFor === innsalgOrgnr ? innsalg : null;
	const innsalgVisningId = activeInnsalg?.vis ? activeInnsalg.visning_id : null;

	// Innsalg: ask Planleggeren (via backend) every time a customer is opened, from the BN redirect or from search
	// (each navigation gets a new location.key, also when the same customer is opened again). Not cached.
	// Wait until the shown account is the one in the url, so no visning is logged for the previous customer.
	// "Tips om AI-mersalg" is only shown when it answers vis: true
	useEffect(() => {
		if (!data?.id || String(data.id) !== params?.id) return;
		const checkKey = `${location.key}:${data.id}:${innsalgOrgnr}`;
		if (innsalgCheckedFor.current === checkKey) return;
		innsalgCheckedFor.current = checkKey;
		// Always clear the previous customer's tips, also when this account has no valid org.nr.
		resetInnsalg();
		setInnsalgNotice(null);
		setShowInnsalg(false);
		if (canLookupInnsalg) getInnsalg(innsalgOrgnr);
	}, [data?.id, params?.id, location.key, canLookupInnsalg, innsalgOrgnr, getInnsalg, resetInnsalg]);

	// Selecting a user in Brukere loads upsellDetails for that user (getUpsellUser), which tells if it is the contact person
	const isContactPersonSelected = correctUpsellId && Boolean(upsellDetails?.is_contact_person);

	// When the caller or the selected user is the contact person, also show the top notice (once per visning)
	useEffect(() => {
		if (!(isContactPersonCalling || isContactPersonSelected) || !innsalgVisningId) return;
		if (innsalgToastedFor.current === innsalgVisningId) return;
		innsalgToastedFor.current = innsalgVisningId;
		setInnsalgNotice(isContactPersonCalling ?
			t('The contact person is calling, see AI upsell tips') :
			t('This user is the contact person, see AI upsell tips'));
	}, [isContactPersonCalling, isContactPersonSelected, innsalgVisningId, t]);

	// Selecting a user that is not the contact person removes the notice, selecting the contact person again shows it again
	useEffect(() => {
		if (isContactPersonCalling || isContactPersonSelected) return;
		innsalgToastedFor.current = null;
		setInnsalgNotice(null);
	}, [isContactPersonCalling, isContactPersonSelected]);

	const onShowInnsalgTips = useCallback((e: React.SyntheticEvent) => {
		e.stopPropagation();
		setInnsalgNotice(null);
		setShowInnsalg(true);
	}, []);

	// this is for bnRedirect checks
	// callerInfo?.account_id === data?.id to check if the caller is the same as the account in data
	// data?.has_upsale to check if the account has upsell options

	// for account search (phone number)
	// callerInfo?.account_id === data?.id && data?.has_upsale;

	return (
		<>
			{
				accountDetails?.note && (
					<StyledHeaderWarning
						direction="column"
						fullWidth
						nowrap
					>
						<Layout justifyContent="start" nowrap direction="row" gap="xs">
							<WarningIcon style={{alignItems: 'start'}} color="white" />
							<Text variant="body1" color="white">{accountDetails.note}</Text>
						</Layout>
					</StyledHeaderWarning>
				)
			}
			{
				activeInnsalg?.statuslinje && (
					<StyledAiLayout
						fullWidth
						direction="row"
						nowrap
						justifyContent="center"
					>
						<Text variant='body1' style={{color: 'white'}}>
							{activeInnsalg.statuslinje}
						</Text>
					</StyledAiLayout>
				)
			}
			<Layout
				fullWidth
				direction="row"
				nowrap
				gap="none"
				onClick={onShowContent}
				style={{
					cursor: 'pointer',
				}}
			>
				<Layout fullWidth direction="column" gap="sm" style={{padding: '5px 16px 16px 16px'}} nowrap>
					<Layout fullWidth direction="column" fullHeight justifyContent="space-between" nowrap>
						<Layout direction="column" fullWidth gap="sm" nowrap>
							<Layout
								direction="row"
								gap="xs"
								fullWidth
								justifyContent="space-between"
								alignItems="end"
								nowrap
							>
								<Layout fullWidth direction="row" nowrap>
									<Text variant='h1' style={{fontSize: '30px'}}>
										{formatInfo(accountDetails?.name)}
									</Text>
									<IconButton onClick={handleCopyCustomerName} size="xs">
										<NewCopyIcon />
									</IconButton>
								</Layout>
								{
									activeInnsalg?.vis && (
										<Layout nowrap>
											<Button
												compact
												variant="secondary"
												icon={<AiSparkleIcon />}
												onClick={onShowInnsalgTips}
											>
												{t('AI upsell tips')}
											</Button>
										</Layout>
									)
								}
								{
									disableUpsell ? null : (
										<Layout nowrap>
											{!correctUpsellId ? (
												<Tooltip text={t('No caller or user selected , please select a user')}>
													<div>
														<UpsellBtn onClick={onShowUpsell} disabled={true} />
													</div>
												</Tooltip>
											) : (
												<UpsellBtn onClick={onShowUpsell} />
											)}
										</Layout>
									)
								}
							</Layout>
							<Layout direction="row" gap="xs" fullWidth>
								<Text variant='body1' color="textSecondary">
									{`${t('Caller')}:`}
								</Text>
								{
									data?.id === callerInfo?.account_id && account && (
										<Layout alignItems="center" gap="xs">
											<Text variant='body1' color="primary">
												{`${callerInfo?.name} (${callerInfo?.phone_number})`}
											</Text>
											{
												isContactPerson && (
													<CrownIcon />
												)
											}
										</Layout>
									)
								}
							</Layout>
						</Layout>
						<Layout direction="column" gap="sm">
							<Layout direction="row" style={{gap: '10px', alignContent: 'start'}}>
								<Layout alignItems="center" style={{gap: '5px'}}>
									<Text>VIP</Text>
									{
										accountDetails?.is_vip_customer ?
											<NewOkIcon /> :
											<NewOkIcon color="#9798A9" />
									}
								</Layout>
								<Layout alignItems="center" style={{gap: '5px'}}>
									<Text>{t('Major customer')}</Text>
									{
										accountDetails?.is_major_customer ?
											<NewOkIcon /> :
											<NewOkIcon color="#9798A9" />
									}
								</Layout>
								<Layout alignItems="center" style={{gap: '5px'}}>
									<Text>{t('Private')}</Text>
									{
										accountDetails?.is_private ?
											<NewOkIcon /> :
											<NewOkIcon color="#9798A9" />
									}
								</Layout>
								<Layout alignItems="center" style={{gap: '5px'}}>
									<Text>{t('SMB')}</Text>
									{
										accountDetails?.is_smb_customer ?
											<NewOkIcon /> :
											<NewOkIcon color="#9798A9" />
									}
								</Layout>
							</Layout>
						</Layout>
					</Layout>
				</Layout>
				<Divider vertical />
				<StyledNpsGrid>
					<StyledNpsNameLayout>
						<Layout
							justifyContent="center"
							alignItems="center"
							nowrap
							gap="none"
							direction="column"
						>
							<Layout>
								<Text
									variant='h1'
									style={{fontSize: '30px', color: isNpsScore?.color}}
								>
									{isNpsScore?.score}
								</Text>
							</Layout>
							<Layout>
								<Text variant='body1' color="textSecondary">
									{t('NPS score')}
								</Text>
							</Layout>
						</Layout>
					</StyledNpsNameLayout>
					<StyledNpsActionLayout>
						<StyledShevronButton $open={showContent}>
							<StyledShevronIcon size="xl" />
						</StyledShevronButton>
					</StyledNpsActionLayout>
					<StyledNpsDivider>
						<Divider />
					</StyledNpsDivider>
					<StyledNpsSatisfactionLayout>
						<Layout
							fullWidth
							justifyContent="center"
							alignItems="center"
							gap="none"
							nowrap
							direction="column"
							onClick={(e: React.SyntheticEvent) => e.stopPropagation()}
							style={{
								cursor: 'default',
								paddingRight: '24px',
							}}
						>
							<Text variant='body1' color="textSecondary">{t('Satisfaction')}</Text>
							<Select
								onChange={handleSatisfactionChange}
								maxDropdownHeight={460}
								value={selectValue}
							>
								{options.map((option) => (
									<ListItem key={option} value={option} primaryText={option} />
								))}
							</Select>
						</Layout>
					</StyledNpsSatisfactionLayout>
				</StyledNpsGrid>
			</Layout>
			{
				showContent && (
					<SmallViewDetails
						data={data}
						showTrustpilot={onShowTrustpilot}
						showBytt={onShowBytt}
					/>
				)
			}
			<Divider />
			{
				!showContent && (
					<>
						<Layout
							fullWidth
							direction="row"
							style={{padding: '16px'}}
						>
							<CustomerLinks
								showTrustpilot={onShowTrustpilot}
								showBytt={onShowBytt}
								data={data}
							/>
						</Layout>
						<Divider />
						<CustomerCompanyInfo data={data} />
						<Divider />
						<CustomerDevices data={data} />
					</>
				)
			}
			<TrustpilotDialog
				show={showTrustpilot}
				onClose={onHideTrustpilot}
			/>
			<ByttDialog
				show={showBytt}
				onClose={onHideBytt}
			/>
			<UpsellDialog
				show={showUpsell}
				onClose={onHideUpsell}
				width={1000}
				header={t('Upselling for Unifon AS')}
				maxHeight={90}
				noPadding
			>
				<UppsellContent />
			</UpsellDialog>
			<InnsalgDialog
				show={showInnsalg}
				onClose={onHideInnsalg}
			/>
			<InnsalgNotice
				message={innsalgNotice}
				onClose={onHideInnsalgNotice}
			/>
		</>
	);
}

// Memo: the customer page re-renders on every tab switch, while this only depends on the account
export default React.memo(CustomerDetails);
